import { autoserialize, autoserializeAs } from 'cerialize';

import { Address, IAddress } from './address';
import { BaseDTO, IBaseDTO } from './base-dto';
import { Charity, ICharity } from './charity';
import { Contact, IContact } from './contact';
import { DonationContent, IDonationContent } from './donation-content';
import { DonationPayment, IDonationPayment } from './donation-payment';
import { DonationReview, IDonationReview } from './donation-review';
import { Lead } from './lead';
import { OwnerType } from './owner';
import { IPaymentDetails, PaymentDetails } from './payment-details';
import { ISpecification, Specification } from './specification';
import { IStandardDonationItem, StandardDonationItem } from './standard-donation-item';
import { IStandardDonationMeta, StandardDonationMeta } from './standard-donation-meta';
import { IStandardDonationReport, StandardDonationReport } from './standard-donation-report';
import { StandardDonationState } from './standard-donation-state';
import { CharityStore, ICharityStore } from './store';
import { ITruck, Truck } from './truck';

export class StandardDonation extends BaseDTO implements IStandardDonation {
  @autoserialize id?: string;
  @autoserializeAs('created_at') createdAt?: string;
  @autoserializeAs('updated_at') updatedAt?: string;
  @autoserializeAs('discarded_at') discardedAt?: string | null;
  @autoserializeAs('aasm_state') aasmState?: StandardDonationState;
  @autoserializeAs('donation_code') donationCode?: string | null;
  @autoserializeAs(StandardDonationMeta, 'meta') meta?: StandardDonationMeta;
  @autoserializeAs('confirmation_code') confirmationCode?: string | null;
  @autoserializeAs('donation_items') donationItems?: string | null;
  @autoserializeAs('donation_notes') donationNotes?: string | null;
  @autoserialize date?: string | null;
  @autoserializeAs('part_of_day') partOfDay?: 'am' | 'pm' | null;
  @autoserializeAs(Contact) donor?: Contact;
  @autoserializeAs(Contact, 'additional_contact') additionalContact?: Contact | null;
  @autoserializeAs(Address) address?: Address;
  @autoserialize instructions?: string | null;
  @autoserialize photos?: string[];
  @autoserializeAs(StandardDonationItem) items?: StandardDonationItem[];
  @autoserializeAs('item_ids') itemIds?: string[];
  @autoserializeAs(Specification) specification?: Specification;
  @autoserialize content?: DonationContent[];
  @autoserializeAs('accepted_content') acceptedContent?: DonationContent[];
  @autoserialize review?: DonationReview;
  @autoserializeAs('expires_at') expiresAt?: string | null;
  @autoserialize staircases?: number;
  @autoserialize elevator?: boolean;
  @autoserializeAs('route_order') routeOrder?: number | null;
  @autoserializeAs('termination_reason') terminationReason?: string | null;
  @autoserializeAs('termination_note') terminationNote?: string | null;
  @autoserializeAs(StandardDonationReport) report?: StandardDonationReport | null;
  @autoserializeAs('charity_id') charityId?: string | null;
  @autoserializeAs(Charity) charity?: Charity | null;
  @autoserializeAs('store_id') storeId?: string | null;
  @autoserializeAs(CharityStore) store?: CharityStore | null;
  @autoserializeAs('charity_vehicle_id') charityVehicleId?: string | null;
  @autoserializeAs(Truck, 'charity_vehicle') charityVehicle?: Truck | null;
  @autoserializeAs('lead_id') leadId?: string | null;
  @autoserializeAs(Lead) lead?: Lead | null;
  @autoserializeAs('owner_id') ownerId?: string | null;
  @autoserializeAs('owner_type') ownerType?: OwnerType;
  // Monetary contribution (RP-3764): amount in cents, plus its payment state.
  @autoserialize contribution?: number | null;
  @autoserializeAs(DonationPayment) payment?: DonationPayment;
  @autoserializeAs(PaymentDetails, 'payment_details') paymentDetails?: PaymentDetails;

  constructor(payload?: IStandardDonation) {
    super(payload);
    this.id = payload?.id?.toString();
    this.createdAt = payload?.createdAt;
    this.updatedAt = payload?.updatedAt;
    this.discardedAt = payload?.discardedAt;
    this.aasmState = payload?.aasmState;
    this.donationCode = payload?.donationCode;
    this.meta = new StandardDonationMeta(payload?.meta);
    this.confirmationCode = payload?.confirmationCode;
    this.donationItems = payload?.donationItems;
    this.donationNotes = payload?.donationNotes;
    this.date = payload?.date;
    this.partOfDay = payload?.partOfDay;
    this.donor = payload?.donor ? new Contact(payload.donor) : undefined;
    this.additionalContact = payload?.additionalContact ? new Contact(payload.additionalContact) : undefined;
    this.address = payload?.address ? new Address(payload.address) : undefined;
    this.instructions = payload?.instructions;
    this.photos = payload?.photos;
    this.items = payload?.items?.map((i) => new StandardDonationItem(i));
    this.itemIds = payload?.itemIds?.map((id) => id?.toString());
    this.specification = payload?.specification ? new Specification(payload.specification) : undefined;
    this.content = payload?.content?.map((c) => new DonationContent(c));
    this.acceptedContent = payload?.acceptedContent?.map((c) => new DonationContent(c));
    this.review = payload?.review ? new DonationReview(payload.review) : undefined;
    this.expiresAt = payload?.expiresAt;
    this.staircases = payload?.staircases;
    this.elevator = payload?.elevator;
    this.routeOrder = payload?.routeOrder;
    this.terminationReason = payload?.terminationReason;
    this.terminationNote = payload?.terminationNote;
    this.report = payload?.report ? new StandardDonationReport(payload.report) : undefined;
    this.charityId = payload?.charityId?.toString();
    this.charity = payload?.charity ? new Charity(payload.charity) : undefined;
    this.storeId = payload?.storeId?.toString();
    this.store = payload?.store ? new CharityStore(payload.store) : undefined;
    this.charityVehicleId = payload?.charityVehicleId?.toString();
    this.charityVehicle = payload?.charityVehicle ? new Truck(payload.charityVehicle) : undefined;
    this.leadId = payload?.leadId?.toString();
    this.lead = payload?.lead ? new Lead(payload.lead) : undefined;
    this.ownerId = payload?.ownerId?.toString();
    this.ownerType = payload?.ownerType;
    this.contribution = payload?.contribution;
    this.payment = payload?.payment ? new DonationPayment(payload.payment) : undefined;
    this.paymentDetails = payload?.paymentDetails ? new PaymentDetails(payload.paymentDetails) : undefined;
  }
}

export interface IStandardDonation extends IBaseDTO {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  discardedAt?: string | null;
  aasmState?: StandardDonationState;
  donationCode?: string | null;
  meta?: IStandardDonationMeta;
  confirmationCode?: string | null;
  donationItems?: string | null;
  donationNotes?: string | null;
  date?: string | null;
  partOfDay?: 'am' | 'pm' | null;
  donor?: IContact;
  additionalContact?: IContact | null;
  address?: IAddress;
  instructions?: string | null;
  photos?: string[];
  items?: IStandardDonationItem[];
  itemIds?: string[];
  specification?: ISpecification;
  content?: IDonationContent[];
  acceptedContent?: IDonationContent[];
  review?: IDonationReview;
  expiresAt?: string | null;
  staircases?: number;
  elevator?: boolean;
  routeOrder?: number | null;
  terminationReason?: string | null;
  terminationNote?: string | null;
  report?: IStandardDonationReport | null;
  charityId?: string | null;
  charity?: ICharity | null;
  storeId?: string | null;
  store?: ICharityStore | null;
  charityVehicleId?: string | null;
  charityVehicle?: ITruck | null;
  leadId?: string | null;
  lead?: Lead | null;
  ownerId?: string | null;
  ownerType?: OwnerType;
  contribution?: number | null;
  payment?: IDonationPayment;
  paymentDetails?: IPaymentDetails;
}
